import { lazy } from 'react';
import type { AppManifest } from '@/apps/types';

/**
 * Winamp 2.
 *
 * A singleton for the same reason Media Player is one: two players fighting
 * over the sound card is nobody's idea of a good time, and every shortcut and
 * every double-clicked track should reach the copy already running.
 *
 * Chromeless: Winamp was skinned, not themed. It never wore a Luna frame, so
 * it draws its own border, caption and caption buttons and the window manager
 * draws nothing at all around it.
 *
 * Not maximizable, because Winamp never was — it was a fixed appliance you
 * parked in a corner. It *is* resizable, because its playlist editor always
 * was, and the three docked windows share this one frame: the player and the
 * equaliser keep their heights and the playlist takes what is left. The width
 * cannot go below the skin's own 275px, which is exactly the constraint the
 * original had.
 *
 * Args:
 *   track     string | number   track id, or index into the playlist.
 *   autoplay  boolean           start it, rather than only select it. The
 *                               shell sets this when a sound file was
 *                               activated, because opening one has always
 *                               meant playing it; the double-click is also
 *                               the gesture browsers want before any audio.
 *                               Launched from the Start menu, Winamp still
 *                               comes up stopped.
 */
export const winampApp: AppManifest = {
  id: 'winamp',
  name: 'Winamp',
  icon: '/assets/icons/winamp.svg',
  component: lazy(() => import('./Winamp')),

  // The skin is 275×116, and with no frame around it that is the window.
  // 348 = the 116px player plus the playlist editor's original 232.
  defaultSize: { w: 275, h: 348 },
  // The floor sits below the rolled-up player, which is the smallest the
  // window ever asks to be: a taller minimum would clamp windowshade halfway
  // and leave the skin sitting in a frame bigger than itself.
  minSize: { w: 275, h: 14 },
  resizable: true,
  maximizable: false,
  chromeless: true,
  singleton: true,

  // Winamp took `.m3u` off Windows on install and never gave it back, and put
  // itself on the Open With list for every sound file it could play.
  openWith: ['.m3u', '.mp3'],

  group: 'Accessories',
  titleFor: () => 'Winamp',
};
